import React, { useState, useMemo } from "react";
import { Box, Button, Collapse, Typography } from "@mui/material";
import {
  BoundingBox,
  CaretDown,
  CaretRight,
  ChatText,
  Plus,
} from "@phosphor-icons/react";
import { Flows } from "../../../../util/Types/Folder";

interface CategorySectionProps {
  title: string;
  items: Flows[];
  onFlowSelect: (flow: any) => void;
  searchTerm: string;
  isDarkMode: boolean;
  palette: any;
  icon: React.ReactNode;
}

export const CategorySection: React.FC<CategorySectionProps> = ({
  title,
  items,
  onFlowSelect,
  searchTerm,
  isDarkMode,
  palette,
  icon,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    return items.filter((item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [items, searchTerm]);

  if (filteredItems.length === 0 && searchTerm) {
    return null;
  }

  const textColor = isDarkMode ? palette.gray[100] : palette.gray[900];
  const itemTextColor = isDarkMode ? palette.gray[200] : palette.gray[700];
  const hoverBg = isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
  const categoryBg = isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)";

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          cursor: "pointer",
          borderRadius: "6px",
          marginBottom: "4px",
          backgroundColor: categoryBg,
          "&:hover": {
            backgroundColor: hoverBg,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {isExpanded ? (
            <CaretDown size={16} color={textColor} weight="bold" />
          ) : (
            <CaretRight size={16} color={textColor} weight="bold" />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {icon}
            <Typography
              sx={{
                color: textColor,
                fontSize: "14px",
                fontWeight: 600,
                letterSpacing: "0.01em",
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                color: isDarkMode ? palette.gray[400] : palette.gray[500],
                fontSize: "12px",
                fontWeight: 500,
                ml: 0.5,
              }}
            >
              ({filteredItems.length})
            </Typography>
          </Box>
        </Box>
      </Box>
      <Collapse in={isExpanded}>
        <Box sx={{ pl: 2, pr: 1 }}>
          {filteredItems.map((flow, index) => (
            <FlowItem
              key={flow.id}
              flow={flow}
              onFlowSelect={onFlowSelect}
              isDarkMode={isDarkMode}
              palette={palette}
              isLast={index === filteredItems.length - 1}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

interface FlowItemProps {
  flow: Flows;
  onFlowSelect: (flow: any) => void;
  isDarkMode: boolean;
  palette: any;
  isLast?: boolean;
}

export const FlowItem: React.FC<FlowItemProps> = ({
  flow,
  onFlowSelect,
  isDarkMode,
  palette,
  isLast = false,
}) => {
  const textColor = isDarkMode ? palette.gray[200] : palette.gray[700];
  const hoverBg = isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
  const iconColor = isDarkMode ? palette.gray[400] : palette.gray[500];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        paddingLeft: "24px",
        borderRadius: "4px",
        marginBottom: isLast ? 0 : "2px",
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: hoverBg,
        },
      }}
      onClick={() => onFlowSelect(flow)}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flex: 1,
          minWidth: 0,
        }}
      >
        <BoundingBox size={16} color={iconColor} weight="regular" />
        <Typography
          sx={{
            color: textColor,
            fontSize: "13px",
            fontWeight: 400,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            letterSpacing: "0.01em",
          }}
        >
          {flow.name}
        </Typography>
      </Box>
      <Button
        variant="text"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onFlowSelect(flow);
        }}
        sx={{
          textTransform: "none",
          minWidth: "auto",
          padding: "4px 12px",
          fontSize: "12px",
          fontWeight: 500,
          color: isDarkMode ? palette.gray[300] : palette.gray[600],
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: isDarkMode
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.06)",
          },
        }}
      >
        <Plus size={14} weight="bold" style={{ marginRight: "4px" }} />
        Add
      </Button>
    </Box>
  );
};
